import React, { useContext, useState } from 'react';
import { useEffect } from 'react';

import {
    Grid,
    Card,
    CardContent,
    Typography,
    Box,
    InputAdornment,
    Avatar,
    Button,
    styled,
    Chip,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import {
    People,
    EventAvailable,
    Diversity3,
    TrendingUp,
} from '@mui/icons-material';
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff';
import DateRangeIcon from '@mui/icons-material/DateRange';
import BasicTable from '../../../components/Table';
import StyledAutocomplete from '../../../components/UIComponents/StyledAutocomplete';
import StyledTextField from '../../../components/UIComponents/StyledTextField';
import CustomCard from '../../../components/UIComponents/CustomCard';
import { runReportData } from '../../../service/network';
import { useLoading } from '../../../context/loading.context';
import { AuthContext } from '../../../context/auth.context';

// Styled components
const StatCard = styled(Card)(({ theme }) => ({
    borderRadius: '16px',
    boxShadow: 'none',
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    transition: 'all 0.3s ease',
    height: '100%',
    minHeight: 90,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: theme.shadows[6],
        borderColor: theme.palette.primary.main
    }
}));

const StatAvatar = styled(Avatar)(({ theme }) => ({
    width: 48,
    height: 48,
    background: 'linear-gradient(135deg, rgba(25,118,210,0.1) 0%, rgba(25,118,210,0.2) 100%)',
    color: theme.palette.primary.main
}));

// Status Badge Component
const StatusBadge = ({ status }) => {
    const getStatusConfig = (status) => {
        const statusText = status?.toString()?.toLowerCase() || '';

        switch (statusText) {
            case 'present':
                return {
                    label: 'Present',
                    color: 'success',
                    icon: <EventAvailable sx={{ fontSize: 16 }} />
                };
            case 'absent':
                return {
                    label: 'Absent',
                    color: 'error',
                    icon: <LogoutIcon sx={{ fontSize: 16 }} />
                };
            case 'on leave':
                return {
                    label: 'On Leave',
                    color: 'warning',
                    icon: <Diversity3 sx={{ fontSize: 16 }} />
                };
            case 'half day':
                return {
                    label: 'Half Day',
                    color: 'info',
                    icon: <HistoryToggleOffIcon sx={{ fontSize: 16 }} />
                };
            case 'work from home':
                return {
                    label: 'WFH',
                    color: 'secondary',
                    icon: <TrendingUp sx={{ fontSize: 16 }} />
                };
            default:
                return {
                    label: status || 'Unknown',
                    color: 'default',
                    icon: null
                };
        }
    };

    const config = getStatusConfig(status);

    return (
        <Chip
            label={config.label}
            color={config.color}
            icon={config.icon}
            size="small"
            variant="filled"
            sx={{
                fontWeight: 600,
                minWidth: 80,
                '& .MuiChip-icon': {
                    marginLeft: '4px'
                }
            }}
        />
    );
};

const getIconforStat = (stat) => {
    switch (stat) {
        case 'Present Records':
            return <EventAvailable sx={{ fontSize: 24 }} color='primary' />
        case 'Half Day Records':
            return <LogoutIcon sx={{ fontSize: 24 }} color='primary' />
        case 'Absent Records':
            return <Diversity3 sx={{ fontSize: 24 }} color='primary' />
        case 'Late Entries':
            return <Diversity3 sx={{ fontSize: 24 }} color='primary' />
        case 'Early Exits':
            return <HistoryToggleOffIcon sx={{ fontSize: 24 }} color='primary' />
        default:
            return <HistoryToggleOffIcon sx={{ fontSize: 24 }} color='primary' />
    }
}

// Format time function to extract only time from datetime string
const formatTime = (datetime) => {
    if (!datetime) return '';
    const parsed = dayjs(datetime);
    return parsed.isValid() ? parsed.format('HH:mm') : datetime;
};
// Cards component
const AttendanceStats = ({ stats }) => {
    return (
        <Grid container spacing={3}>
            {stats.map((card, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                    <StatCard sx={{ width: '100%', minWidth: 240 }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box>
                                    <Typography variant="h5" fontWeight="bold" color={card.indicator}>
                                        {card.value}
                                    </Typography>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        {card.label}
                                    </Typography>
                                </Box>
                                <StatAvatar>
                                    {getIconforStat(card.label)}
                                </StatAvatar>
                            </Box>
                        </CardContent>
                    </StatCard>
                </Grid>
            ))}
        </Grid>
    );
};

const tableConfig = {
    rows: [
        {
            id: 'id',
            label: 'Employee ID',
            accessor: 'employee',
            flex: 1,
            minWidth: '120px',
            align: 'left',
        },
        {
            id: 'name',
            label: 'Name',
            accessor: 'employee_name',
            flex: 1.5,
            minWidth: '180px',
            align: 'left',
        },
        {
            id: 'date',
            label: 'Date',
            accessor: 'date',
            flex: 1.5,
            minWidth: '180px',
            align: 'center',
            // Format date to show only date part
            Cell: ({ value }) => value ? dayjs(value).format('YYYY-MM-DD') : ''
        },
        {
            id: 'branch',
            label: 'Branch',
            accessor: 'branch',
            flex: 1,
            minWidth: '120px',
            align: 'center',
        },
        {
            id: 'checkIn',
            label: 'Check In',
            accessor: 'in_time',
            flex: 1,
            minWidth: '120px',
            align: 'center',
            Cell: ({ value }) => formatTime(value)
        },
        {
            id: 'checkOut',
            label: 'Check Out',
            accessor: 'out_time',
            flex: 1,
            minWidth: '120px',
            align: 'center',
            Cell: ({ value }) => formatTime(value)
        },
        {
            id: 'calculated_status',
            label: 'Calculated Status',
            accessor: 'calculated_status',
            flex: 1,
            minWidth: '120px',
            align: 'center',
        },
        {
            id: 'status',
            label: 'Status',
            accessor: 'status',
            flex: 1,
            minWidth: '120px',
            align: 'center',
            // Add custom cell renderer for status badges
            Cell: ({ value }) => <StatusBadge status={value} />
        }
    ]
};

// Main component
export default function Attendance_Alteration() {
    const [startDate, setStartDate] = useState({
        startDate: dayjs().format('YYYY-MM-DD')
    });
    const [filteredData, setFilteredData] = useState(tableConfig.columns);
    const [data, setData] = useState({});
    const { selLocation } = useContext(AuthContext);
    const { showLoader, hideLoader } = useLoading();

    useEffect(() => {
        showLoader();
        let req_body = {
            report_name: "Attendance Alteration Report",
            filters: { "attendance_date": dayjs(startDate.startDate).format('YYYY-MM-DD'), "company": selLocation[0] },
            ignore_prepared_report: false,
            are_default_filters: false
        };
        runReportData(req_body).then((resp) => {
            setData(resp.data.message);
            hideLoader();
        }).catch((err) => {
            hideLoader();
            console.log(err);
        });
    }, [startDate,selLocation])

    const handleChange = (field, value) => {
        setStartDate(prev => ({
            ...prev,
            [field]: value
        }));
    };

const exportToExcel = () => {
    if (!data.result || data.result.length === 0) {
        alert('No data to export');
        return;
    }

    try {
        // Use tableConfig.rows to decide what to export
        const worksheetData = data.result.map(item => {
            const row = {};
            tableConfig.rows.forEach(col => {
                if (col.accessor === 'in_time' || col.accessor === 'out_time') {
                    row[col.label] = formatTime(item[col.accessor]);
                } else if (col.accessor === 'date') {
                    row[col.label] = item.date ? dayjs(item.date).format('YYYY-MM-DD') : '';
                } else {
                    row[col.label] = item[col.accessor] || '';
                }
            });
            return row;
        });

        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(worksheetData);

        worksheet['!cols'] = tableConfig.rows.map(r => ({ wch: 20 })); // auto width
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Attendance Alteration Report');

        const fileName = `Attendance_Alteration_Report_${dayjs(startDate.startDate).format('YYYY-MM-DD')}.xlsx`;
        XLSX.writeFile(workbook, fileName);

    } catch (error) {
        console.error('Error exporting to Excel:', error);
        alert('Error exporting data. Please try again.');
    }
};

    return (
        <Box
            sx={{
                width: '100%',
                p: { xs: 1, sm: 2 },
                boxSizing: 'border-box'
            }}
        >
            <CustomCard
                bgColor="light"
                shadow="lg"
                borderRadius="xxl"
                sx={{
                    width: '100%',
                    minHeight: 800,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    p: 3
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 3,
                        flexWrap: 'wrap',
                        gap: 2
                    }}
                >
                    <Box sx={{ marginTop: 3, }}>
                        <Typography variant="subTitle" gutterBottom color='primary.main'>
                            Attendance Alteration Report
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Overview of Employee Attendance Alteration Report
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 3 }}>
                        <Grid size={7}>
                            <StyledTextField
                                label="Select Date"
                                type="date"
                                value={startDate.startDate}
                                onChange={(e) => handleChange('startDate', e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <DateRangeIcon color="action" />
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                        <Grid size={3}>
                            <Button
                                variant="contained"
                                color="primary"
                                sx={{
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    px: 3,
                                    marginTop: 3
                                }}
                                onClick={exportToExcel}
                                disabled={!data.result || data.result.length === 0}
                            >
                                Export
                            </Button>
                        </Grid>
                    </Box>
                </Box>

                <Box sx={{ mt: 2, mb: 2 }}>
                    {/* Stats section can be added here if needed */}
                </Box>
                <Box sx={{ flex: 1, }}>
                    <BasicTable
                        data={{ ...tableConfig, columns: data.result ?? [] }}
                        backgroundColor="theme=.main.lightColor"
                    />
                </Box>
            </CustomCard>
        </Box>
    );
}